/*
  Maison d'Esteban — oferta 2+1 în pagină (P13.7 + P13.10, 10 oct 2026). Modul ES, idempotent.

  1. <est-kit-bar> (snippets/est-kit-bar.liquid): contorul lipit jos „1/3 → 2/3 → 3/3 · Ai deblocat 2+1”.
     Citește coșul real (/cart.js). Se actualizează la `est:atc` (card, PDP, configurator), la evenimentele coșului
     Tinker (care vin ÎNAINTE ca /cart.js să se schimbe → re-citire până se schimbă semnătura, practici §6) și la pageshow.
  2. <est-kit-builder> (sections/est-kit-builder.liquid): „Alege 3” — sloturi, picker cu căutare după original
     și gen, adaugă toate parfumurile într-un singur POST /cart/add.js anunțat prin CartLinesUpdateEvent
     (același drum ca butonul „Adaugă” de pe card: bulina din antet și sertarul se actualizează, sertarul se deschide).
*/
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const fmt = (cents) => {
  const v = Math.round(cents) / 100;
  return (Number.isInteger(v) ? String(v) : v.toFixed(2).replace('.', ',')) + ' lei';
};
const norm = (s) => (s || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();

let cartPromise = null;
async function getCart(fresh) {
  if (fresh || !cartPromise) {
    cartPromise = fetch(`${ROOT}cart.js?t=${Date.now()}`, { headers: { Accept: 'application/json' }, cache: 'no-store', credentials: 'same-origin' })
      .then((r) => r.json())
      .catch(() => null);
  }
  return cartPromise;
}

function openCartDrawer() {
  const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
  if (drawer && typeof drawer.open === 'function') { drawer.open(); return true; }
  return false;
}

/* ---------------- 1. contorul 2+1 ---------------- */
if (!customElements.get('est-kit-bar')) {
  class EstKitBar extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this._sig = null;
      this.size = Number(this.dataset.size || 3) || 3;
      this.max = Number(this.dataset.max || 5000);
      this.settle = this.settle.bind(this);
      this.refresh = this.refresh.bind(this);
      for (const n of ['est:atc', 'shopify:cart:lines-update', 'cart:update', 'cart-section:restored', 'est:kit-added']) {
        document.addEventListener(n, this.settle);
      }
      import('@shopify/events')
        .then((m) => { const n = m?.StandardEvents?.cartLinesUpdate; if (n && n !== 'shopify:cart:lines-update') document.addEventListener(n, this.settle); })
        .catch(() => {});
      window.addEventListener('pageshow', () => this.refresh(true));
      this.querySelector('[data-est-kit-cart]')?.addEventListener('click', (e) => {
        if (openCartDrawer()) e.preventDefault();
      });
      this.refresh(false);
    }
    async settle() {
      if (this._settling) { this._again = true; return; }
      this._settling = true;
      try {
        const before = this._sig;
        for (const wait of [200, 450, 800, 1500]) {
          await sleep(wait);
          await this.refresh(true);
          if (this._sig !== before) break;
        }
      } finally { this._settling = false; }
      if (this._again) { this._again = false; this.settle(); }
    }
    async refresh(fresh) {
      const cart = await getCart(fresh);
      if (!cart || !Array.isArray(cart.items)) return;
      this._sig = `${cart.item_count}|${cart.total_price}`;
      const count = cart.items.reduce((n, it) => {
        const p = it.original_price ?? it.price;
        return n + (p > 0 && p <= this.max ? it.quantity : 0);
      }, 0);
      this.render(count);
    }
    render(count) {
      const size = this.size;
      const inSet = count === 0 ? 0 : count % size || size;
      const sets = Math.floor(count / size);
      const free = sets;
      const paid = sets * (size - 1);
      const ds = this.dataset;
      let msg;
      if (count === 0) msg = ds.msgZero;
      else if (inSet === size) msg = ds.msgDone;
      else if (sets >= 1) msg = ds.msgNext;
      else msg = size - inSet === 1 ? ds.msgTwo : ds.msgOne;
      msg = (msg || '').replace('[n]', String(size - inSet)).replace('[platite]', String(paid)).replace('[gratuite]', String(free));
      this.querySelectorAll('[data-slot]').forEach((s, i) => s.classList.toggle('is-full', i < inSet));
      const n = this.querySelector('[data-n]');
      if (n) n.textContent = String(inSet);
      const m = this.querySelector('[data-msg]');
      if (m && m.textContent !== msg) m.textContent = msg;
      this.classList.toggle('is-done', inSet === size);
      this.hidden = count === 0 && !ds.empty;
    }
  }
  customElements.define('est-kit-bar', EstKitBar);
}

/* ---------------- 2. configuratorul „Alege 3” ---------------- */
if (!customElements.get('est-kit-builder')) {
  class EstKitBuilder extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      try { this.items = JSON.parse(this.querySelector('[data-kit-products]')?.textContent || '[]'); } catch (e) { this.items = []; }
      try { this.t = JSON.parse(this.querySelector('[data-kit-i18n]')?.textContent || '{}'); } catch (e) { this.t = {}; }
      this.byV = new Map(this.items.map((p) => [String(p.v), p]));
      this.packs = [...this.querySelectorAll('[data-pack]')];
      this.size = Number(this.packs.find((b) => b.getAttribute('aria-checked') === 'true')?.dataset.pack || this.dataset.size || 3);
      this.picks = [];
      this.active = 0;
      this.gender = '';
      this.slotsEl = this.querySelector('[data-kit-slots]');
      this.sheet = this.querySelector('[data-kit-sheet]');
      this.list = this.querySelector('[data-kit-list]');
      this.q = this.querySelector('[data-kit-q]');
      this.cta = this.querySelector('[data-kit-cta]');
      this.err = this.querySelector('[data-kit-err]');

      this.packs.forEach((b) => b.addEventListener('click', () => this.setPack(Number(b.dataset.pack))));
      this.querySelectorAll('[data-kit-g]').forEach((b) => b.addEventListener('click', () => {
        this.gender = b.dataset.kitG;
        this.querySelectorAll('[data-kit-g]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
        this.renderList();
      }));
      let tmr;
      this.q?.addEventListener('input', () => { clearTimeout(tmr); tmr = setTimeout(() => this.renderList(), 80); });
      this.q?.closest('form')?.addEventListener('submit', (e) => e.preventDefault());
      this.querySelector('[data-kit-close]')?.addEventListener('click', () => this.sheet.close());
      this.sheet?.addEventListener('click', (e) => { if (e.target === this.sheet) this.sheet.close(); });
      this.sheet?.addEventListener('close', () => this._opener?.focus());
      this.list?.addEventListener('click', (e) => {
        const b = e.target instanceof Element && e.target.closest('[data-kit-pick]');
        if (b) this.pick(b.dataset.kitPick);
      });
      this.slotsEl?.addEventListener('click', (e) => {
        const t = e.target instanceof Element ? e.target : null;
        const rm = t?.closest('[data-kit-rm]');
        if (rm) { this.remove(Number(rm.dataset.kitRm)); return; }
        const sl = t?.closest('[data-kit-slot]');
        if (sl) this.open(Number(sl.dataset.kitSlot), sl);
      });
      this.cta?.addEventListener('click', () => this.addAll());
      this.render();
    }
    setPack(n) {
      this.size = n;
      this.packs.forEach((b) => b.setAttribute('aria-checked', String(Number(b.dataset.pack) === n)));
      this.picks = this.picks.slice(0, n);
      this.render();
    }
    open(i, opener) {
      this.active = Math.min(i, this.size - 1);
      this._opener = opener;
      const title = this.querySelector('[data-kit-sheet-title]');
      if (title) title.textContent = (this.t.slotOf || 'Parfumul [i] din [n]').replace('[i]', String(this.active + 1)).replace('[n]', String(this.size));
      this.renderList();
      if (!this.sheet.open) this.sheet.showModal();
      if (window.matchMedia('(min-width: 750px)').matches) this.q?.focus();
    }
    pick(v) {
      const p = this.byV.get(String(v));
      if (!p) return;
      this.picks[this.active] = p;
      this.err.hidden = true;
      const next = [...Array(this.size).keys()].find((k) => !this.picks[k]);
      this.render();
      if (next === undefined) { this.sheet.close(); this.cta?.focus(); }
      else {
        this.active = next;
        const title = this.querySelector('[data-kit-sheet-title]');
        if (title) title.textContent = (this.t.slotOf || '').replace('[i]', String(next + 1)).replace('[n]', String(this.size));
        this.renderList();
        this.list.scrollTop = 0;
      }
    }
    remove(i) {
      this.picks[i] = undefined;
      this.render();
      this.slotsEl.querySelector(`[data-kit-slot="${i}"]`)?.focus();
    }
    renderList() {
      const q = norm(this.q?.value);
      const words = q.split(' ').filter(Boolean);
      const out = this.items.filter((p) => {
        if (this.gender && !(p.g || '').includes(this.gender)) return false;
        if (!words.length) return true;
        const hay = norm(`${p.o} ${p.h} ${p.n}`);
        return words.every((w) => hay.includes(w));
      });
      const chosen = new Set(this.picks.filter(Boolean).map((p) => String(p.v)));
      const esc = (s) => String(s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      this.list.innerHTML = out.length
        ? out.slice(0, 120).map((p) => `<li class="estkb-it${chosen.has(String(p.v)) ? ' is-chosen' : ''}">
            <img src="${esc(p.i)}" alt="" width="64" height="64" loading="lazy">
            <span class="estkb-it__t">${p.o ? `<small>${esc(this.t.notes || 'Note comune cu')} <b>${esc(p.o)}</b>${p.h ? ` · ${esc(p.h)}` : ''}</small>` : ''}<span>${esc(p.n)}</span></span>
            <button type="button" class="estkb-it__b" data-kit-pick="${p.v}" aria-label="${esc(this.t.pick || 'Alege')}: ${esc(p.n)}${p.o ? ` (${esc(p.o)})` : ''}">${esc(this.t.pick || 'Alege')}</button>
          </li>`).join('')
        : `<li class="estkb-empty">${esc(this.t.noResults || '')}</li>`;
      const c = this.querySelector('[data-kit-results]');
      if (c) c.textContent = (this.t.results || '[x] parfumuri').replace('[x]', String(out.length));
    }
    render() {
      const esc = (s) => String(s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      let html = '';
      for (let i = 0; i < this.size; i++) {
        const p = this.picks[i];
        const free = i === this.size - 1 || (this.size === 6 && i === 2);
        if (p) {
          html += `<li class="estkb-slot is-full">
            <button type="button" class="estkb-slot__b" data-kit-slot="${i}" aria-label="${esc(this.t.change || 'Schimbă')}: ${esc(p.n)}">
              <img src="${esc(p.i)}" alt="" width="120" height="120" loading="lazy">
              <span class="estkb-slot__o">${esc(p.o || p.n)}</span><span class="estkb-slot__n">${esc(p.n)}</span>
            </button>
            <button type="button" class="estkb-slot__x" data-kit-rm="${i}" aria-label="${esc(this.t.remove || 'Scoate')}: ${esc(p.n)}"><svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/></svg></button>
            ${free ? `<em class="estkb-slot__free">${esc(this.t.free || 'gratuit')}</em>` : ''}
          </li>`;
        } else {
          html += `<li class="estkb-slot"><button type="button" class="estkb-slot__b estkb-slot__b--empty" data-kit-slot="${i}">
            <span class="estkb-slot__plus" aria-hidden="true">+</span><span>${esc((this.t.slot || 'Parfumul [i]').replace('[i]', String(i + 1)))}</span>
          </button>${free ? `<em class="estkb-slot__free">${esc(this.t.free || 'gratuit')}</em>` : ''}</li>`;
        }
      }
      this.slotsEl.innerHTML = html;
      this.slotsEl.style.setProperty('--n', String(Math.min(this.size, 3)));
      const n = this.picks.filter(Boolean).length;
      const pack = this.packs.find((b) => Number(b.dataset.pack) === this.size);
      const total = Number(pack?.dataset.price || 0);
      const count = this.querySelector('[data-kit-count]');
      if (count) count.textContent = (this.t.chosen || '[x] din [n]').replace('[x]', String(n)).replace('[n]', String(this.size));
      const done = n === this.size;
      this.cta.disabled = !done;
      this.cta.textContent = done
        ? (this.t.add || 'Adaugă în coș · [p]').replace('[p]', fmt(total))
        : (this.t.pickMore || 'Mai alege [k]').replace('[k]', String(this.size - n));
      this.classList.toggle('is-done', done);
    }
    async addAll() {
      if (this._busy) return;
      const picks = this.picks.filter(Boolean);
      if (picks.length !== this.size) return;
      this._busy = true;
      this.cta.disabled = true;
      this.cta.textContent = this.t.adding || 'Se adaugă…';
      this.err.hidden = true;
      const qty = new Map();
      picks.forEach((p) => qty.set(p.v, (qty.get(p.v) || 0) + 1));
      const items = [...qty].map(([id, quantity]) => ({ id: Number(id), quantity }));
      const sectionIds = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
      let mod = null, deferred = null;
      try { mod = await import('@shopify/events'); } catch (e) { mod = null; }
      if (mod?.CartLinesUpdateEvent) {
        deferred = mod.CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(new mod.CartLinesUpdateEvent({ action: 'add', context: 'product', lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })), promise: deferred.promise }));
      }
      try {
        const addUrl = window.Theme?.routes?.cart_add_url || `${ROOT}cart/add.js`;
        const res = await fetch(addUrl, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: sectionIds.join(',') }),
        }).then((r) => r.json());
        if (res.status) throw new Error(res.description || res.message || 'cart');
        const cart = await getCart(true);
        if (deferred && cart) {
          deferred.resolve({ cart: mod.CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'est-kit', itemCount: picks.length, sections: res.sections, didError: false } });
        }
        if (!deferred) { if (!openCartDrawer()) location.href = `${ROOT}cart`; }
        /* măsurare (P10.5): câte o adăugare pe produs, cu sursa „kit” */
        picks.forEach((p) => document.dispatchEvent(new CustomEvent('est:atc', { detail: { source: 'kit', productId: p.pid, name: p.n, price: p.price / 100, items: [{ id: Number(p.v), quantity: 1 }] } })));
        this.cta.textContent = this.t.added || 'Adăugat';
        setTimeout(() => { this.picks = []; this._busy = false; this.render(); }, 1600);
      } catch (e) {
        deferred?.reject(e);
        this.err.textContent = this.t.error || 'Nu am putut adăuga în coș.';
        this.err.hidden = false;
        this._busy = false;
        this.render();
      }
    }
  }
  customElements.define('est-kit-builder', EstKitBuilder);
}
